import React from 'react';
import { formatDateTime } from '../../../../utils/shortcuts';
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { useReactToPrint } from 'react-to-print';

export default function FormatToPDFPreview({ data, isLoading, setResultData }) {
    const [filename, setFilename] = React.useState(data?.title ?? 'Monthly Vehicle KM Report');
    const [notes, setNotes] = React.useState(data?.report?.map(item => item.note || ''));
    const [startKm, setStartKm] = React.useState(data?.report?.map(item => item.vehicle.start_km || 0));
    const [endKm, setEndKm] = React.useState(data?.report?.map(item => item.vehicle.end_km || 0));
    const [totalKm, setTotalKm] = React.useState(data?.report?.map(item => item.vehicle.total_km || 0));
    const [editMode, setEditMode] = React.useState({ row: null, field: null });
    const [lastEdited, setLastEdited] = React.useState(null);
    const contentRef = React.useRef();

    const [hideTotalKM, setHideTotalKM] = React.useState(false);
    const [hideETEKM, setHideETEKM] = React.useState(false);

    // Calculate Overall Total KM based on the totalKm array
    const totalKM = totalKm.reduce((sum, km) => sum + (km || 0), 0);


    // Handle changes for notes, start KM, end KM, and total KM
    const handleFieldChange = (index, field, value) => {
        if (field === 'note') {
            const newNotes = [...notes];
            newNotes[index] = value;
            setNotes(newNotes);
        } else if (field === 'startKm') {
            const newStartKm = [...startKm];
            newStartKm[index] = Number(value);
            setStartKm(newStartKm);
            if (endKm[index]) {
                const newTotalKm = [...totalKm];
                newTotalKm[index] = newStartKm[index] - endKm[index];
                setTotalKm(newTotalKm);
            }
        } else if (field === 'endKm') {
            const newEndKm = [...endKm];
            newEndKm[index] = Number(value);
            setEndKm(newEndKm);
            if (startKm[index]) {
                const newTotalKm = [...totalKm];
                newTotalKm[index] = newEndKm[index] - startKm[index];
                setTotalKm(newTotalKm);
            }
        } else if (field === 'totalKm') {
            const newTotalKm = [...totalKm];
            newTotalKm[index] = Number(value);
            setTotalKm(newTotalKm);
        }
        setLastEdited(index);
    };

    // Handle double-click to enter edit mode
    const handleDoubleClick = (index, field) => {
        setEditMode({ row: index, field });
    };

    // Handle blur to exit edit mode
    const handleBlur = () => {
        setEditMode({ row: null, field: null });
    };

    // Export to CSV (unchanged)
    const exportToCSV = (apiData, fileName) => {
        // Your existing exportToCSV implementation
    };

    // Handle print (unchanged)
    const handlePrint = useReactToPrint({ contentRef });

    // Calculate total kilometers and EOE end kilometers (unchanged)
    // const totalKM = data?.report?.reduce((sum, entry) => sum + (entry.vehicle?.total_km || 0), 0) || 0;
    let eoe_start_km = data?.report?.length > 0 ? data.report[0]?.vehicle?.start_km : 0;
    if (eoe_start_km === 0) {
        for (let i = 1; i < data?.report?.length; i++) {
            if (data.report[i]?.vehicle?.start_km > 0) {
                eoe_start_km = data.report[i].vehicle.start_km;
                break;
            }
        }
    }

    let eoe_end_km = data?.report?.length > 0 ? data.report[data?.report?.length - 1]?.vehicle?.end_km : 0;
    if (eoe_end_km === 0) {
        for (let i = data?.report?.length - 2; i >= 0; i--) {
            if (data.report[i]?.vehicle?.end_km > 0) {
                eoe_end_km = data.report[i].vehicle.end_km;
                break;
            }
        }
    }

    return (
        <div>
            <div className="row card p-4">
                <div className='d-flex justify-content-between mb-2'>
                    <div></div>
                    <h5 className="mb-3 text-center text-dark">Preview & Edit</h5>
                    <div className='d-flex gap-2'>
                        <button onClick={(e) => exportToCSV(data, filename)} disabled={false} className='btn btn-success btn-sm'>
                            <i className="material-icons">download</i>Download
                        </button>
                        <button onClick={handlePrint} className='btn btn-dark btn-sm'>
                            <i className="material-icons">print</i>Print / PDF
                        </button>
                    </div>
                </div>
                <hr />
                <div className="modal-body" style={{ width: '100%' }} ref={contentRef}>
                    <div className='text-center'>
                        <h6 className='py-3' style={{ background: '#9bc1e6' }}>{filename}</h6>
                    </div>
                    <div className='py-3'>
                        <div className='row'>
                            <div className='col-sm-4'>
                                <div className='text-center'>
                                    <img src="/assets/images/report/logo.png" alt="logo" className='img-fluid' />
                                </div>
                            </div>
                            <div className='col-sm-8'>
                                <div className=''>
                                    <table className='table table-bordered table-hover table-striped d-table text-center'>
                                        <thead>
                                            <tr>
                                                <th style={{ background: '#001e62', color: '#fff' }}>Vehicle No</th>
                                                <th style={{ background: '#001e62', color: '#fff' }}>Owner Name & No</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>{data?.get_vehicle?.vehicle_name ?? '-'}</td>
                                                <td>{data?.get_vehicle?.owner_name ?? '-'} @ {data?.get_vehicle?.owner_contact ?? '-'}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover table-striped d-table mb-0">
                            <thead>
                                <tr>
                                    <th style={{ background: 'yellow', color: '#000' }}>Date</th>
                                    <th style={{ background: 'yellow', color: '#000' }}>Driver</th>
                                    <th style={{ background: 'yellow', color: '#000' }}>Start KM</th>
                                    <th style={{ background: 'yellow', color: '#000' }}>End KM</th>
                                    <th style={{ background: 'yellow', color: '#000' }}>Total KM</th>
                                    <th width="200" style={{ background: 'yellow', color: '#000' }}>Note</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data?.report?.map((item, index) => (
                                    <tr
                                        key={index}
                                        // onDoubleClick={() => handleDoubleClick(index, 'note')}
                                        style={{ backgroundColor: lastEdited === index ? '#e0f7fa' : 'transparent' }}
                                    >
                                        <td style={{ width: '20%' }}>{formatDateTime(item.date)}</td>
                                        <td style={{ width: '20%' }}>{data?.get_vehicle?.driver_name} @ {data?.get_vehicle?.driver_contact}</td>
                                        <td onDoubleClick={() => handleDoubleClick(index, 'startKm')} style={{ width: '10%' }}>
                                            {editMode.row === index && editMode.field === 'startKm' ? (
                                                <input
                                                    type="number"
                                                    className='form-control'
                                                    value={startKm[index]}
                                                    onChange={(e) => handleFieldChange(index, 'startKm', e.target.value)}
                                                    onBlur={handleBlur}
                                                    autoFocus
                                                />
                                            ) : (
                                                startKm[index]
                                            )}
                                        </td>
                                        <td onDoubleClick={() => handleDoubleClick(index, 'endKm')} style={{ width: '10%' }}>
                                            {editMode.row === index && editMode.field === 'endKm' ? (
                                                <input
                                                    type="number"
                                                    value={endKm[index]}
                                                    className='form-control'
                                                    onChange={(e) => handleFieldChange(index, 'endKm', e.target.value)}
                                                    onBlur={handleBlur}
                                                    autoFocus
                                                />
                                            ) : (
                                                endKm[index]
                                            )}
                                        </td>
                                        <td onDoubleClick={() => handleDoubleClick(index, 'totalKm')} style={{ width: '10%' }}>
                                            {editMode.row === index && editMode.field === 'totalKm' ? (
                                                <input
                                                    type="number"
                                                    className='form-control'
                                                    value={totalKm[index]}
                                                    onChange={(e) => handleFieldChange(index, 'totalKm', e.target.value)}
                                                    onBlur={handleBlur}
                                                    autoFocus
                                                />
                                            ) : (
                                                totalKm[index]
                                            )}
                                        </td>
                                        <td onDoubleClick={() => handleDoubleClick(index, 'note')} style={{ width: '30%' }}>
                                            {editMode.row === index && editMode.field === 'note' ? (
                                                <input
                                                    type="text"
                                                    value={notes[index]}
                                                    className='form-control'
                                                    onChange={(e) => handleFieldChange(index, 'note', e.target.value)}
                                                    onBlur={handleBlur}
                                                    autoFocus
                                                />
                                            ) : (
                                                notes[index]
                                            )}
                                        </td>
                                    </tr>
                                ))}

                                {!hideTotalKM && (
                                    <tr onDoubleClick={() => setHideTotalKM(true)}>
                                        <td colSpan={3}></td>
                                        <td><span className='bold'>Overall Total KM</span></td>
                                        <td><span className='bold'>{totalKM}</span></td>
                                        <td></td>
                                    </tr>
                                )}
                                {!hideETEKM && (
                                    <tr onDoubleClick={() => setHideETEKM(true)}>
                                        <td colSpan={3}></td>
                                        <td><span className='bold' title='End to End KM'>ETE KM</span></td>
                                        <td><span className='bold'>{eoe_end_km}</span></td>
                                        <td></td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        <div className=''>
                            <h5 className='py-2 text-center mb-0 border' style={{ background: '#ddd' }}>Signature</h5>
                        </div>
                        <div className="table-responsive text-center">
                            <table className="table table-hover table-striped d-table mb-0">
                                <thead>
                                    <tr>
                                        <th className='border px-3 py-2' style={{ color: '#000' }}>Vendor</th>
                                        <th className='border px-3 py-2' style={{ color: '#000' }}>Mr. Debashis Mitra</th>
                                        <th className='border px-3 py-2' style={{ color: '#000' }}>Mr. Sushanta Roy</th>
                                        <th className='border px-3 py-2' style={{ color: '#000' }}>Docket Service & Operation Team</th>
                                        <th className='border px-3 py-2' style={{ color: '#000' }}>Approval By Sir</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className='border px-3 py-4'><span></span></td>
                                        <td className='border px-3 py-4'><span></span></td>
                                        <td className='border px-3 py-4'><span></span></td>
                                        <td className='border px-3 py-4'><span></span></td>
                                        <td className='border px-3 py-4'><span></span></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}